import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import Breadcrumbs from "../based/Breadcrumbs";
import CommonTable from "../based/CommonTable";
import CommonModal from "../based/Modal";
import { INITPAGING, NOTIFY } from "../based/Constants";
import { ConfirmDialog } from "../based/Dialog";
import Loading from "../based/Loading";
import { Notify } from "../based/Notify";
import EmbroideryServices from "../based/services/EmbroideryServices";

const HOOP_TYPE = {
    Square: 1,
    Round: 2,
};

const HOOP_TYPE_OPTIONS = [
    { value: HOOP_TYPE.Square, label: "Hoop vuông" },
    { value: HOOP_TYPE.Round, label: "Hoop tròn" },
];

const DEFAULT_PAGING = { ...INITPAGING, pageSize: 20 };

// Tạo model rỗng cho modal thêm mới, tách riêng để sau nối API vẫn dùng cùng shape dữ liệu.
function createEmptyHoop() {
    return {
        id: 0,
        name: "",
        hoopType: HOOP_TYPE.Square,
        widthMm: 295,
        heightMm: 295,
        diameterMm: 205,
        safeWidthMm: 230,
        safeHeightMm: 230,
        safeDiameterMm: 160,
        cornerRadiusMm: 80,
        sortOrder: 0,
        isActive: true,
    };
}

// Chuẩn hóa số nhập từ form, cho phép rỗng để admin nhìn rõ field chưa cấu hình.
function normalizeNumber(value) {
    if (value === "" || value === null || value === undefined) return "";
    const parsed = Number(value);
    return Number.isNaN(parsed) ? "" : parsed;
}

// Hiển thị kích thước hoop theo đúng loại khung đang cấu hình.
function formatDimension(item) {
    if (item.hoopType == HOOP_TYPE.Round) return `${item.diameterMm || 0} mm`;
    return `${item.widthMm || 0} x ${item.heightMm || 0} mm`;
}

// Hiển thị safezone theo đúng loại khung đang cấu hình.
function formatSafeZone(item) {
    if (item.hoopType == HOOP_TYPE.Round) return `${item.safeDiameterMm || 0} mm`;
    return `${item.safeWidthMm || 0} x ${item.safeHeightMm || 0} mm`;
}

// Lấy tên loại hoop để render table và select.
function getHoopTypeText(type) {
    const option = HOOP_TYPE_OPTIONS.find((x) => x.value == type);
    return option ? option.label : "";
}

// Màn quản lý khung thêu theo Figma section 1, dữ liệu được lấy từ API Embroidery.
function EmbroideryHoopManagement() {
    const [datas, setDatas] = useState([]);
    const [paging, setPaging] = useState(DEFAULT_PAGING);
    const [editingHoop, setEditingHoop] = useState(null);
    const [isShowModal, setIsShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMsg, setLoadingMsg] = useState("Đang tải...");

    useEffect(() => {
        _getData(DEFAULT_PAGING);
    }, []);

    // Lấy danh sách hoop từ API paging, dùng chung cho load đầu trang và refresh sau khi lưu/xóa.
    async function _getData(newPaging) {
        const requestPaging = newPaging || paging || DEFAULT_PAGING;
        setIsLoading(true);
        setLoadingMsg("Đang tải danh sách khung thêu...");
        const [err, data] = await EmbroideryServices.GetHoops(requestPaging);
        if (!err && data) {
            setDatas(data.listObjects || []);
            setPaging(data.paging || requestPaging);
        } else {
            setDatas([]);
            setPaging(DEFAULT_PAGING);
            Notify(NOTIFY.WARNING, "Có lỗi", "Không tải được danh sách khung thêu.");
        }
        setIsLoading(false);
    }

    // Mở modal thêm mới với bộ default theo thiết kế Figma.
    function _openCreateModal() {
        setEditingHoop(createEmptyHoop());
        setIsShowModal(true);
    }

    // Mở modal sửa bằng dữ liệu chi tiết mới nhất từ API.
    async function _openEditModal(item) {
        setIsLoading(true);
        setLoadingMsg("Đang tải thông tin khung thêu...");
        const [err, data] = await EmbroideryServices.GetHoopById(item.id);
        setIsLoading(false);
        if (!err && data) {
            setEditingHoop({ ...data });
            setIsShowModal(true);
        } else {
            Notify(NOTIFY.WARNING, "Có lỗi", "Không tải được thông tin khung thêu.");
        }
    }

    // Đóng modal và xóa model tạm để lần mở sau không giữ dữ liệu cũ.
    function _handleCloseModal() {
        setIsShowModal(false);
        setEditingHoop(null);
    }

    // Lưu hoop qua API create/update, sau đó refresh lại paging hiện tại để table đồng bộ.
    async function _handleSaveHoop(model) {
        setIsLoading(true);
        setLoadingMsg("Đang lưu khung thêu...");
        const submitModel = {
            ...model,
            sortOrder: model.sortOrder || 0,
            isActive: model.isActive !== false,
        };
        const [err, data] = await EmbroideryServices.CreateUpdateHoop(submitModel);
        setIsLoading(false);
        if (err || !data) {
            Notify(NOTIFY.WARNING, "Có lỗi", "Không lưu được khung thêu.");
            return;
        }
        _handleCloseModal();
        _getData(paging);
    }

    // Xóa hoop qua API với confirm, thành công thì refresh lại table.
    function _handleDeleteHoop(id) {
        ConfirmDialog("Xác nhận xóa?", "Xin vui lòng xác nhận để xóa khung thêu này", () => {
            return new Promise(async (resolve, reject) => {
                setIsLoading(true);
                setLoadingMsg("Đang xóa khung thêu...");
                const [err, data] = await EmbroideryServices.DeleteHoop(id);
                setIsLoading(false);
                if (err || !data) {
                    Notify(NOTIFY.WARNING, "Có lỗi", "Không xóa được khung thêu.");
                    reject({ title: "Thất bại", msg: "Vui lòng thử lại sau." });
                    return;
                }
                _getData(paging);
                resolve({ title: "Thành công", msg: "Xóa thành công." });
            });
        });
    }

    // Render action đầu bảng giống style admin hiện tại.
    function _renderAction() {
        return (
            <div className="mb-2">
                <button className="btn btn-custom" onClick={_openCreateModal}>
                    Thêm khung thêu +
                </button>
            </div>
        );
    }

    // Render header table theo Figma Config khung thêu.
    function _renderHeader() {
        return (
            <thead>
                <tr className="text-center">
                    <th style={{ minWidth: 180 }}>Tên khung thêu (Hoop)</th>
                    <th style={{ width: 110 }}>Hình</th>
                    <th style={{ minWidth: 160 }}>Loại hoop</th>
                    <th style={{ minWidth: 180 }}>Kích thước khung thêu</th>
                    <th style={{ minWidth: 160 }}>Safezone</th>
                    <th style={{ minWidth: 160 }}>Bán kính bo góc</th>
                    <th style={{ width: 190 }}>Thao tác</th>
                </tr>
            </thead>
        );
    }

    // Render body table từ dữ liệu hoop hiện tại, bao gồm preview hình hoop theo loại.
    function _renderBody() {
        return (
            <tbody>
                {datas && datas.length > 0 ? (
                    datas.map((item) => {
                        return (
                            <tr key={item.id}>
                                <td className="vertical-align-middle">{item.name}</td>
                                <td className="text-center vertical-align-middle">
                                    <HoopPreview item={item} compact />
                                </td>
                                <td className="text-center vertical-align-middle">{getHoopTypeText(item.hoopType)}</td>
                                <td className="text-center vertical-align-middle">{formatDimension(item)}</td>
                                <td className="text-center vertical-align-middle">{formatSafeZone(item)}</td>
                                <td className="text-center vertical-align-middle">{item.hoopType == HOOP_TYPE.Square ? `${item.cornerRadiusMm || 0} mm` : ""}</td>
                                <td className="text-center vertical-align-middle">
                                    <button className="btn btn-custom m-1" onClick={() => _openEditModal(item)}>
                                        Sửa
                                    </button>
                                    <button className="btn btn-danger m-1" onClick={() => _handleDeleteHoop(item.id)}>
                                        Xóa
                                    </button>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan={7} className="text-center">
                            Không có dữ liệu
                        </td>
                    </tr>
                )}
            </tbody>
        );
    }

    return (
        <React.Fragment>
            <Breadcrumbs currentPage="Config khung thêu" />
            <Loading show={isLoading} msg={loadingMsg} />
            <Wrapper>
                <CommonTable data={datas} paging={paging} hideSearch freeSize renderAction={_renderAction} renderHeader={_renderHeader} renderBody={_renderBody} onFilter={_getData} />
            </Wrapper>
            <HoopModal isShowModal={isShowModal} model={editingHoop} onClose={_handleCloseModal} onSave={_handleSaveHoop} />
        </React.Fragment>
    );
}

export default EmbroideryHoopManagement;

// Vẽ preview hoop bằng CSS để UI không phụ thuộc asset Figma hết hạn.
function HoopPreview({ item, compact }) {
    const isRound = item.hoopType == HOOP_TYPE.Round;
    const sizeText = isRound ? item.diameterMm : item.widthMm || item.heightMm;
    return (
        <HoopPreviewWrapper className={compact ? "compact" : ""}>
            <div className={isRound ? "hoop-shape round" : "hoop-shape square"} style={!isRound ? { borderRadius: `${Math.max(18, Math.min(item.cornerRadiusMm || 48, 80))}px` } : null}>
                <span className="hoop-name">{item.name ? item.name.replace("Hoop ", "") : "E"}</span>
                <span className="hoop-line"></span>
                <span className="hoop-size">{sizeText || 0}mm</span>
            </div>
        </HoopPreviewWrapper>
    );
}

HoopPreview.propTypes = {
    item: PropTypes.object.isRequired,
    compact: PropTypes.bool,
};

// Modal thêm/sửa hoop, đổi field theo loại vuông/tròn đúng context Figma.
function HoopModal({ isShowModal, model, onClose, onSave }) {
    const [formModel, setFormModel] = useState(createEmptyHoop());

    React.useEffect(() => {
        if (isShowModal && model) setFormModel({ ...model });
    }, [isShowModal, model]);

    const isRound = formModel.hoopType == HOOP_TYPE.Round;
    const isValid = formModel.name && (isRound ? formModel.diameterMm > 0 && formModel.safeDiameterMm > 0 : formModel.widthMm > 0 && formModel.heightMm > 0 && formModel.safeWidthMm > 0 && formModel.safeHeightMm > 0);

    // Cập nhật một field trong form, tự chuẩn hóa số nếu input đang là number.
    function _handleChange(field, value, isNumber) {
        setFormModel((prev) => ({ ...prev, [field]: isNumber ? normalizeNumber(value) : value }));
    }

    // Đổi loại hoop và giữ default riêng cho loại được chọn để form không bị trống ngoài ý muốn.
    function _handleTypeChange(type) {
        setFormModel((prev) => ({
            ...prev,
            hoopType: type,
            widthMm: prev.widthMm || 295,
            heightMm: prev.heightMm || 295,
            diameterMm: prev.diameterMm || 205,
            safeWidthMm: prev.safeWidthMm || 230,
            safeHeightMm: prev.safeHeightMm || 230,
            safeDiameterMm: prev.safeDiameterMm || 160,
        }));
    }

    // Render input có hậu tố mm, dùng chung cho các field kích thước.
    function _renderUnitInput(field, value) {
        return (
            <div className="unit-input">
                <input type="number" className="form-control" value={value} onChange={(e) => _handleChange(field, e.target.value, true)} />
                <span>mm</span>
            </div>
        );
    }

    // Render nhóm field cho hoop vuông/chữ nhật.
    function _renderSquareFields() {
        return (
            <React.Fragment>
                <div className="row">
                    <div className="col-6 form-group">
                        <label className="required">Chiều ngang khung thêu</label>
                        {_renderUnitInput("widthMm", formModel.widthMm)}
                    </div>
                    <div className="col-6 form-group">
                        <label className="required">Chiều dọc khung thêu</label>
                        {_renderUnitInput("heightMm", formModel.heightMm)}
                    </div>
                </div>
                <div className="row">
                    <div className="col-6 form-group">
                        <label className="required">Safezone chiều ngang</label>
                        {_renderUnitInput("safeWidthMm", formModel.safeWidthMm)}
                    </div>
                    <div className="col-6 form-group">
                        <label className="required">Safezone chiều dọc</label>
                        {_renderUnitInput("safeHeightMm", formModel.safeHeightMm)}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    // Render nhóm field cho hoop tròn.
    function _renderRoundFields() {
        return (
            <React.Fragment>
                <div className="row">
                    <div className="col-6 form-group">
                        <label className="required">Đường kính khung thêu</label>
                        {_renderUnitInput("diameterMm", formModel.diameterMm)}
                    </div>
                </div>
                <div className="row">
                    <div className="col-6 form-group">
                        <label className="required">Đường kính Safezone</label>
                        {_renderUnitInput("safeDiameterMm", formModel.safeDiameterMm)}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <HoopModalGlobalStyle />
            <CommonModal show={isShowModal} id="embroidery-hoop-modal" title={formModel.id > 0 ? "Cập nhật khung thêu" : "Thêm khung thêu"} onClose={onClose} hiddenFooter size="md">
                <ModalContent>
                    <div className="hoop-type-row">
                        {HOOP_TYPE_OPTIONS.map((item) => {
                            return (
                                <label key={item.value} className="hoop-radio">
                                    <input type="radio" checked={formModel.hoopType == item.value} onChange={() => _handleTypeChange(item.value)} />
                                    <span>{item.label}</span>
                                </label>
                            );
                        })}
                    </div>
                    <div className="form-group">
                        <label className="required">Tên khung thêu (Hoop)</label>
                        <input type="text" className="form-control" value={formModel.name || ""} onChange={(e) => _handleChange("name", e.target.value)} />
                    </div>
                    {isRound ? _renderRoundFields() : _renderSquareFields()}
                    <div className="row align-items-start">
                        <div className="col-6 form-group">
                            <label>Hình hoop</label>
                            <div className="hoop-upload-preview">
                                <HoopPreview item={formModel} />
                                <button type="button" className="remove-preview" title="Xóa hình">
                                    ×
                                </button>
                            </div>
                        </div>
                        {!isRound && (
                            <div className="col-6 form-group">
                                <label className="required">Bán kính độ bo của góc</label>
                                {_renderUnitInput("cornerRadiusMm", formModel.cornerRadiusMm)}
                            </div>
                        )}
                    </div>
                    <div className="modal-action-row">
                        <button className="btn btn-danger" onClick={onClose}>
                            Đóng
                        </button>
                        <button className="btn btn-custom" disabled={!isValid} onClick={() => onSave(formModel)}>
                            Lưu
                        </button>
                    </div>
                </ModalContent>
            </CommonModal>
        </React.Fragment>
    );
}

HoopModal.propTypes = {
    isShowModal: PropTypes.bool,
    model: PropTypes.object,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
};

const HoopModalGlobalStyle = createGlobalStyle`
    #embroidery-hoop-modal .modal-dialog {
        max-width: 589px;
    }

    #embroidery-hoop-modal .modal-content {
        border-radius: 0;
        overflow: hidden;
    }

    #embroidery-hoop-modal .modal-header {
        background: #64c5b1;
        border-bottom: 1px solid #d9d9d9;
        min-height: 63px;
        align-items: center;
    }

    #embroidery-hoop-modal .modal-title {
        color: #ffffff;
        font-size: 20px;
        font-weight: 600;
        line-height: 20px;
    }
`;

const Wrapper = styled.div`
    /* Section quản lý hoop cần mở hết chiều ngang theo layout admin và không dùng khung scroll ngang. */
    .card-box {
        max-width: none;
        width: auto;
    }

    .table th {
        background: #d9d9d9;
        color: #676767;
        font-size: 14px;
        font-weight: 600;
        vertical-align: middle;
    }

    .table td {
        color: #676767;
        font-size: 14px;
        height: 80px;
    }
`;

const HoopPreviewWrapper = styled.div`
    width: 230px;
    height: 160px;
    border: 1px solid #d9d9d9;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &.compact {
        width: 68px;
        height: 68px;
        border: 0;
        margin: 0 auto;
    }

    .hoop-shape {
        width: 124px;
        height: 124px;
        border: 6px solid #1f8a59;
        color: #111111;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: relative;
        box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.75);
    }

    .hoop-shape.round {
        border-radius: 50%;
    }

    .hoop-shape:before,
    .hoop-shape:after {
        content: "";
        width: 38px;
        height: 10px;
        background: #e8e8ec;
        border: 1px solid #d9d9d9;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
    }

    .hoop-shape:before {
        top: -10px;
    }

    .hoop-shape:after {
        bottom: -10px;
    }

    .hoop-name {
        font-size: 28px;
        font-weight: 700;
        line-height: 1;
        margin-bottom: 10px;
    }

    .hoop-line {
        width: 84px;
        height: 1px;
        background: #111111;
        margin-bottom: 8px;
    }

    .hoop-size {
        font-size: 15px;
        font-weight: 700;
    }

    &.compact .hoop-shape {
        width: 56px;
        height: 56px;
        border-width: 2px;
        border-radius: 17px !important;
    }

    &.compact .hoop-shape.round {
        border-radius: 50% !important;
    }

    &.compact .hoop-shape:before,
    &.compact .hoop-shape:after,
    &.compact .hoop-line,
    &.compact .hoop-size {
        display: none;
    }

    &.compact .hoop-name {
        font-size: 13px;
        margin: 0;
    }
`;

const ModalContent = styled.div`
    color: #676767;
    font-size: 18px;
    padding-bottom: 0;

    label {
        font-weight: 600;
        margin-bottom: 10px;
    }

    label.required:after {
        content: " *";
        color: #d70000;
    }

    .form-control {
        border-radius: 0;
        border-color: #d9d9d9;
        color: #676767;
        font-size: 18px;
        height: 34px;
    }

    .hoop-type-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 48px;
        margin: 2px 0 28px;
    }

    .hoop-radio {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
        font-size: 18px;
        font-weight: 600;
    }

    .hoop-radio input {
        width: 18px;
        height: 18px;
        accent-color: #64c5b1;
    }

    .unit-input {
        position: relative;
    }

    .unit-input input {
        padding-right: 48px;
    }

    .unit-input span {
        color: #bebebe;
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
    }

    .hoop-upload-preview {
        display: inline-block;
        position: relative;
    }

    .remove-preview {
        position: absolute;
        top: 5px;
        right: 8px;
        border: 0;
        background: transparent;
        color: #ff1b36;
        font-size: 18px;
        line-height: 1;
        padding: 0;
    }

    .modal-action-row {
        border-top: 1px solid #d9d9d9;
        margin: 18px -15px -15px;
        padding: 14px 30px;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
    }

    .modal-action-row .btn {
        min-width: 146px;
        height: 35px;
        border-radius: 0;
        font-size: 16px;
        font-weight: 700;
    }
`;
